import {
  SUBSCRIPTION_STATUS,
  SubscriptionKeyListType,
  SubscriptionKeyListResponse,
} from "api/interfaces/subscription.interface";
import {
  convertMsToDays,
  getAccountTimeFormatDate,
  isWithinDays,
  substractTimeFromNow,
} from "./timeUtil";
import { formatUppercaseOnly } from "./functions";
import NoSubscriptonLabel from "pages/subscription/modal/common/NoSubscriptonLabel";
import ExpiredDate from "pages/subscription/modal/common/ExpiredDate";
import { COLORS } from "styles/colors";

export const subscriptionKeyFormat = "1XXXX-XXXX-XXXX-XXXX";

// channelExpire 가 아직 지나지 않아 Usage Plan 전환이 대기중일 때 공통으로 쓰는 경고 문구
export const USAGE_PLAN_SWITCH_WARNING_TEXT =
  "Will switch to Usage Plan once the expiration date passes.";

// autoEnroll 이 켜져 있어도 channelExpire 가 아직 지나지 않았으면
// Usage Plan 전환이 대기중인 상태로 본다.
export const isPendingUsagePlanSwitch = ({
  autoEnroll,
  channelExpire,
}: {
  autoEnroll?: boolean;
  channelExpire?: string;
}): boolean => {
  return (
    Boolean(autoEnroll) &&
    !!channelExpire &&
    getActivateStatus({
      activateDate: String(new Date()),
      expireDate: channelExpire,
    }) !== SUBSCRIPTION_STATUS.EXPIRED_END
  );
};

/**
 *
 * @param v
 * @returns boolean
 * @description key 가 XX-XXXX(+X)X-XXXX-XXXX-XXXX-XXXX 타입인지 확인하는함수
 *
 */
export const isValidKeyFormat = (v: unknown): v is string => {
  return (
    typeof v === "string" &&
    /^[1-3][A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}$/.test(v)
  );
};
// Activate Status 조건 계산하는 함수
export const getActivateStatus = ({
  activateDate,
  expireDate,
}: {
  activateDate?: string;
  expireDate?: string;
}): SUBSCRIPTION_STATUS => {
  let tempStatus: SUBSCRIPTION_STATUS = SUBSCRIPTION_STATUS.NONE;

  if (expireDate !== undefined) {
    expireDate = expireDate.replace("T00:00:00.000", "T23:59:59.999");
    tempStatus = SUBSCRIPTION_STATUS.ACTIVATED;

    if (
      convertMsToDays(-substractTimeFromNow(expireDate)) >= 31 &&
      convertMsToDays(-substractTimeFromNow(expireDate)) <= 60
    ) {
      tempStatus = SUBSCRIPTION_STATUS.EXPIRED_WARNING;
    }
    if (
      convertMsToDays(-substractTimeFromNow(expireDate)) >= 15 &&
      convertMsToDays(-substractTimeFromNow(expireDate)) < 31
    ) {
      tempStatus = SUBSCRIPTION_STATUS.EXPIRED_SOON;
    }
    if (convertMsToDays(-substractTimeFromNow(expireDate)) < 15) {
      tempStatus = SUBSCRIPTION_STATUS.EXPIRED;
    }
    if (convertMsToDays(-substractTimeFromNow(expireDate)) < 1) {
      tempStatus = SUBSCRIPTION_STATUS.EXPIRED_END;
    }
  }

  return tempStatus;
};

// extendTargetKey(연장된 대상 키)가 있으면 만료 경고 상태를 무시하고 ACTIVATED 로 표시
export const getEffectiveStatus = (
  status: SUBSCRIPTION_STATUS,
  extendTargetKey?: string
): SUBSCRIPTION_STATUS => {
  return extendTargetKey ? SUBSCRIPTION_STATUS.ACTIVATED : status;
};

// Activate UI 텍스트화
export const showActivateStatus = (status: SUBSCRIPTION_STATUS) => {
  switch (status) {
    case SUBSCRIPTION_STATUS.NONE:
      return "";
    default:
      return "Activated";
    // case SUBSCRIPTION_STATUS.EXPIRED_SOON:
    //   return "Expires Soon";
    // default:
    //   return formatUppercaseOnly(status);
  }
};

// 만료 임박/만료 alert 아이콘에 표시할 tooltip 텍스트
export const getExpiredTooltipText = (status: SUBSCRIPTION_STATUS) => {
  switch (status) {
    case SUBSCRIPTION_STATUS.EXPIRED_WARNING:
      return "Renewal Reminder";
    case SUBSCRIPTION_STATUS.EXPIRED_SOON:
      return "Expiring Soon";
    case SUBSCRIPTION_STATUS.EXPIRED:
      return "Expiring Very Soon";
    case SUBSCRIPTION_STATUS.EXPIRED_END:
      return "Expired";
    default:
      return "";
  }
};

// replace key 가능한지 확인하는 함수
export const isEnableReplace = (
  keyResponse: SubscriptionKeyListResponse
): boolean => {
  if (
    getActivateStatus(keyResponse) !== SUBSCRIPTION_STATUS.NONE ||
    !isWithinDays(keyResponse.createdAt, 30) || // 생성일 30일 이후
    keyResponse.replaceKey //replace 기록 있는지)
  ) {
    return false;
  }
  return true;
};

// channel Expiration Date 로 Subscription 상태 확인
export const checkSubscriptionStatusFromExpireDate = ({
  status,
  expiredDate,
}: {
  status?: SUBSCRIPTION_STATUS;
  expiredDate?: string;
}) => {
  if (
    !expiredDate ||
    (expiredDate && convertMsToDays(-substractTimeFromNow(expiredDate)) < 0)
  ) {
    return <NoSubscriptonLabel />;
  }

  return (
    <ExpiredDate
      status={status ?? SUBSCRIPTION_STATUS.NONE}
      expiredDate={expiredDate}
    />
  );
};

// error code
// DUPLICATE_SERVICE_NAME(120001,"Service is duplicated"),
// DUPLICATE_SERVICE_TIER(120002,"Price Tier is duplicated"),
// SUBSCRIPTION_NOT_FOUND(121000,"Subscription not found"),
// SUBSCRIPTION_FAIL_GENERATE_KEY(121003,"Generating key is failed."),
// NOT_EXIST_DISCOUNT_ORG(121004,"There are no price information from organization."),
// ERROR_CHECKOUT_PROCESS(121005,"There was an issue processing the payment."),
// ERROR_UNKNOWN_KEY_TYPE(121006,"The process was halted due to an undefined key type."),
// EMPTY_REQUEST_SUBSCRIPTION_KEY(121007,"Subscription key not found."),
// NOT_REGISTERED_REQUEST_SUBSCRIPTION_KEY(121008,"Subscription key not registered."),
// ALREADY_REGISTERED_REQUEST_SUBSCRIPTION_KEY(121009,"Subscription key is already registered."),
// UNDEFINED_SUBSCRIPTION_KEY_TYPE(121010,"Subscription Type is undefined."),
// ALREADY_ACTIVATE_SUBSCRIPTION_KEY(121011,"Subscription Type is already activated."),
// NOT_EU_ORGANIZATION_DETAIL_SUBSCRIPTION(121012,"Only EU organizations can searchable"),
// NOT_CP_ORGANIZATION_DETAIL_SUBSCRIPTION(121013,"Only CP organizations can searchable"),
// FAIL_TO_SEND_EMAIL_SUBSCRIPTION(121014,"Fail to send email."),
// FAIL_TO_API_STRIPE_PRICE(121015,"Fail to update price using Stripe API"),
// EMPTY_PRICE_DATA_STRIPE(121016,"Service price not found."),
// FAIL_TO_UPDATE_STRIPE_PRICE(121017,"Fail to update price."),
// NOT_MATCH_REGISTERED_CP(121018,"The registered key is not associated with the parent CP."),
// CP_ORGANIZATION_NOT_FOUND(121019,"Unable to retrieve CP information."),
// EXCEL_GENERATION_FAIL(121020,"Export failed while creating the file"),

export const storageGraph = [
  {
    key: "used",
    label: "Used",
    color: COLORS.BUTTONS2,
  },
  {
    key: "total",
    label: "Total",
    color: COLORS.GREEN,
  },
];
